import React, { Suspense, useEffect, useRef, useState } from "react";
import FormCreator from "../../components/forms/FormCreator";
import FinalMolGrid from "../../components/FinalMolGrid/FinalMolGrid";
import Buttons from "../../components/ui/button/Buttons";
import { paginationObj } from "../../utils/Constants";
import { useGetAllMembershipTaskListMutation, useLazyDeleteTaskListDetailsOfMemberByIdQuery, useLazyGetAllDropDownsForTaskListQuery } from "../../app/services/MembershipTaskList/MembershipTaskListServiceApi";
import { setAllDropDownFieldsBatch } from "../../utils/FormFields/FieldsSetting/SetFieldSetting";
import DataLoader from "../../components/FinalMolGrid/ui/dataLoader/DataLoader";
import { useNavigate, useParams } from "react-router-dom";
import { encryptAESID } from "../../utils/Encryption";
import { MembershipTaskListConfig, MembershipTaskListFormData } from "./data/MembershipTaskListForm.Data";
import SwalAlert from '../../services/swalService/SwalService';
import { getAuditContext } from "../../lib/authenticationLibrary";

const MembershipTaskListForm = () => {
  const taskListFilterRef = useRef();
  const [viewMembershipTask, setviewMembershipTask] = useState(MembershipTaskListFormData);
  const molGridRef = useRef();
  const navigate = useNavigate();
  const params = useParams();
  const { confirm, success, error } = SwalAlert();
  const audit = getAuditContext();

  const [dataSource, setDataSource] = useState();
  const [pagination, setPagination] = useState({
    ...paginationObj,
    ApplicationStatusId: null,
    Email: null,
    MembershipTypeId: null,
  });

  const taskListDropDowns = [
    { dropdownName: "MembershipTypeForTaskList", fieldId: "membershipType" },
    { dropdownName: "ApplicationStatus", fieldId: "applicationStatus" },
  ];

  // API Calls
  const [getTaskList, { isLoading: taskListIsLoading, data: taskListData, isSuccess: taskListIsSuccess }] = useGetAllMembershipTaskListMutation();
  const [getTaskListDropDown, { isLoading: taskListDropDownIsLoading, data: taskListDropDownData, isSuccess: taskListDropDownIsSuccess }] = useLazyGetAllDropDownsForTaskListQuery();
  const [deleteMembershipTaskListQuery, { isLoading: isDeleteMemberLoading, isSuccess: isDeleteMemberSuccess, data: deleteMemberResponse }] = useLazyDeleteTaskListDetailsOfMemberByIdQuery();

  useEffect(() => {
    getTaskListDropDown();
    getTaskListData(pagination);
  }, []);

  // useEffects
  useEffect(() => {
    if (!taskListIsLoading && taskListData && taskListIsSuccess) {
      setDataSource(taskListData.itemList)
      setPagination(prev => {
        return { ...prev, totalCount: taskListData.totalCount }
      });
    }
  }, [taskListIsLoading, taskListData, taskListIsSuccess]);

  // Effect for set dropdown
  useEffect(() => {
    if (!taskListDropDownIsLoading && taskListDropDownIsSuccess && taskListDropDownData) {
      // Updates dropdown fields in the form based on the fetched data
      setAllDropDownFieldsBatch(MembershipTaskListFormData, taskListDropDowns, taskListDropDownData);
    }
  }, [taskListDropDownIsLoading, taskListDropDownIsSuccess, taskListDropDownData]);

  /* Handle Delete Member Response */
  useEffect(() => {
    if (!isDeleteMemberLoading && isDeleteMemberSuccess && deleteMemberResponse) {
      if (!deleteMemberResponse.hasError) {
        success("Record Deleted Successfully.");
        getTaskListData(pagination);
      } else {
        error(deleteMemberResponse.errorMessage);
      }
    }
  }, [isDeleteMemberLoading, isDeleteMemberSuccess, deleteMemberResponse]);

  // Functions
  const getTaskListData = (data) => {
    getTaskList(data);
  }

  const handlePageChange = (page) => {
    const { pageNumber, pageSize } = page;
    getTaskListData({ ...pagination, pageSize: pageSize, pageNo: pageNumber })
  };

  const searchClick = () => {
    let formData = taskListFilterRef?.current?.getFormData();
    let request = {
      ...paginationObj,
      ApplicationStatusId: formData?.applicationStatus?.value || null,
      Email: formData?.email || null,
      MembershipTypeId: formData?.membershipType?.value || null,
    }
    setPagination(prev => { return { ...prev, ...request } });
    getTaskListData(request);
  };

  const handleEditClick = (rowData, index) => {
    const { ipasMemberId, applicationTaskListId } = rowData;
    if (ipasMemberId && applicationTaskListId)
      navigate(`/EditMembershipTaskList/${encryptAESID(ipasMemberId)}/${encryptAESID(applicationTaskListId)}`)
  };

  const handleActionChange = (action, data, formData) => {
    if (action === "VIEW_INDIVIDUAL_INFORMATION") {
      window.open(`/EditViewIndividualMember/${params.memberId}`, "_blank")
    }
  }

  /* Delete Member Handler */
  const handleDeleteClick = (data) => {
    confirm('Delete?', 'Are you sure you want to Delete?', 'Delete', 'Cancel').then((confirmed) => {
      if (confirmed) {
        const request = { id: data?.applicationTaskListId !== null ? data?.applicationTaskListId : 0,
                          id2: 0,
                          id3: audit.adminUserId,
                          id4: audit.appId};
        deleteMembershipTaskListQuery(request)
      }
    });
  };

  const handleClearClick = () => {
    const formData = { ...viewMembershipTask };
    formData.initialState = {
      email: null,
      membershipType: null,
      applicationStatus: null,
    }
    let request = {
      ...paginationObj,
      ApplicationStatusId: null,
      Email: null,
      MembershipTypeId: null,
    }
    setPagination(prev => { return { ...prev, ...request } });
    getTaskListData(request);
    setviewMembershipTask(formData);
  }

  const actionHandler = {
    EDIT: handleEditClick,
    DELETE: handleDeleteClick
  };

  return (
    <div>
      <div className="membership-application">
        <div className="form-card">
          {taskListDropDownData &&
            <Suspense fallback={<DataLoader />}>
              <div className="">
                <FormCreator
                  config={viewMembershipTask}
                  ref={taskListFilterRef}
                  key={false}
                  onActionChange={handleActionChange}
                />
                <div className="col-lg-12 btn-sec mt-001">
                  <Buttons
                    onClick={handleClearClick}
                    buttonText={"Clear"}
                    buttonTypeClassName="back-btn btn mt-auto mr-4"
                    isLoading={false}
                  />
                  <Buttons
                    onClick={searchClick}
                    buttonText={"Search"}
                    buttonTypeClassName="theme-btn btn mt-auto"
                    isLoading={taskListIsLoading}
                  />
                </div>
              </div>
            </Suspense>
          }
          <div className="table-100 mt-4">
            <h5 className="align-self-end mb-1">Total Count: {taskListData?.totalCount || 0}</h5>
            <FinalMolGrid
              ref={molGridRef}
              configuration={MembershipTaskListConfig}
              dataSource={dataSource}
              isLoading={taskListIsLoading}
              allowPagination={true}
              pagination={pagination}
              onActionChange={actionHandler}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipTaskListForm;
